import { Colors } from "../configs/colors";
import { monthNames } from "../data";

export const getTenantId = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  if (parts.length >= 2) {
    const subdomain = parts[0];

    if (subdomain && subdomain !== "www") {
      return subdomain;
    }
  }

  return null;
};

// Maps environment-variant subdomains to their base tenant.
// presto-dev / presto-qa / presto-staging → presto
// New tenants get the right theme/data automatically without code changes.
const ENV_SUFFIXES = ['-dev', '-qa', '-staging', '-uat', '-test'];

export const resolveBaseTenant = (tenantId: string): string => {
  for (const suffix of ENV_SUFFIXES) {
    if (tenantId.endsWith(suffix)) {
      return tenantId.slice(0, -suffix.length);
    }
  }
  return tenantId;
};
export const formatPhoneNumber = (
  phoneNumberString: string | undefined | null
) => {
  if (
    phoneNumberString === null ||
    phoneNumberString === "" ||
    phoneNumberString === undefined
  ) {
    return "N/A";
  }

  const digitsOnly = phoneNumberString.replace(/\D/g, "");

  if (digitsOnly.length < 10) {
    return "invalid Phone Number";
  }

  if (digitsOnly.length === 10) {
    return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(
      3,
      6
    )} ${digitsOnly.slice(6, 10)}`;
  }

  return `${digitsOnly.slice(1, 4)} ${digitsOnly.slice(
    4,
    7
  )} ${digitsOnly.slice(7, 11)}`;
};

export const formatDate = (
  dateInput: string | number | Date | null | undefined
) => {
  if (dateInput === null || dateInput === "" || dateInput === undefined) {
    return "N/A";
  }
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "N/A";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
};

export const formatDateShort = (
  dateInput: string | number | Date | null | undefined
) => {
  if (dateInput === null || dateInput === "" || dateInput === undefined) {
    return "N/A";
  }
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "N/A";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

export const formatDateTime = (dateString: string | undefined) => {
  if (dateString === null || dateString === "" || dateString === undefined) {
    return "N/A";
  }
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export const padText = (
  text: number | string,
  padTextCount: number,
  direction: "end" | "start" = "end",
  fillText: string = " "
) => {
  if (direction === "end") {
    return text.toString().padEnd(padTextCount, fillText);
  } else {
    return text.toString().padStart(padTextCount, fillText);
  }
};

export const getAccentColor = (bgColor: string) => {
  const rgb = parseInt(bgColor.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? Colors.BLACK : Colors.WHITE;
};

export function formatDateNow() {
  const date = new Date();

  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  const hours = date.getHours() % 12 || 12;
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  function getMonthName(month: any) {
    return monthNames[month - 1];
  }

  const formattedDate = `${day}-${getMonthName(
    month
  )}-${year} ${hours}.${minutes}${ampm}`;

  return formattedDate;
}

export const formatUSDate = (date: Date | string | number) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "N/A";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
};
export const formatUS12HourTime = (date: Date): string => {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  let ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;
  let minutesStr = minutes < 10 ? "0" + minutes : minutes.toString();

  let formattedTime = `${hours}:${minutesStr} ${ampm}`;
  return formattedTime;
};

interface IFormatCurrencyProps {
  amount: number;
  prefix?: string;
}

export const formatCurrency = ({ amount, prefix }: IFormatCurrencyProps) => {
  const newPrefix = prefix || "";
  const formattedAmount: string = amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${newPrefix}${formattedAmount}`;
};

export const getCashierInitials = (
  name: string | null | undefined
): string => {
  if (!name) return "";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0][0]?.toUpperCase() ?? "";
  return `${words[0][0] ?? ""}${words[words.length - 1][0] ?? ""}`.toUpperCase();
};

export const formatCashierColumn = (
  checkinName: string | null | undefined,
  pickupName: string | null | undefined
): string => {
  const checkinInitials = getCashierInitials(checkinName);
  const pickupInitials = getCashierInitials(pickupName);
  return pickupInitials ? `${checkinInitials}/${pickupInitials}` : checkinInitials;
};

export const handleTabAndEnter = (
  refs: React.RefObject<HTMLElement>[],
  event: any,
  index: number
) => {
  if (
    event.type === "blur" ||
    (event.type === "keydown" &&
      (event.key === "Enter" ||
        event.key === "Tab" ||
        event.key === "ArrowDown"))
  ) {
    event.preventDefault();
    let nextIndex;
    if (refs.length - 1 === index) {
      nextIndex = index;
    } else {
      nextIndex = index + 1;
      if (refs.length - 1 === nextIndex) return;

      while (
        refs[nextIndex]?.current &&
        isElementDisabled(refs[nextIndex].current)
      ) {
        if (refs.length - 1 === nextIndex) break;
        nextIndex++;
      }
    }

    console.log(
      `${nextIndex} ${isElementDisabled(refs[nextIndex]?.current || null)} ${
        refs[nextIndex]?.current
      }`
    );

    if (isElementDisabled(refs[nextIndex]?.current || null)) return;

    const element = refs[nextIndex]?.current;
    if (element && typeof element.focus === "function") {
      element.focus();
    }
  }
};

const isElementDisabled = (element: HTMLElement | null): boolean => {
  if (!element) {
    return false;
  }

  if (element instanceof HTMLInputElement) {
    return element.disabled;
  }

  if (element instanceof HTMLButtonElement) {
    return element.disabled;
  }

  if (
    element.getAttribute &&
    element.getAttribute("aria-disabled") === "true"
  ) {
    return true;
  }

  if (element.hasAttribute && element.hasAttribute("disabled")) {
    return true;
  }

  return false;
};
